import { Component } from "@theme/component";
import {
  CartAddEvent,
  CartUpdateEvent,
  DiscountUpdateEvent,
  QuantitySelectorUpdateEvent,
  ThemeEvents,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  debounce,
  fetchConfig,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
} from "@theme/utilities";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate
    );
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange
    );
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      // Find postcard information for this line item before removing
      const postcardInfo = this.findPostcardInfo(line);

      if (postcardInfo.hasPostcard && postcardInfo.postcardKey) {
        // Remove with postcard information
        return this.onLineItemRemove({
          lineIndex: line,
          postcardKey: postcardInfo.postcardKey,
          hasPostcard: true,
          itemKey: postcardInfo.itemKey,
        });
      } else {
        // Remove just the single item
        return this.onLineItemRemove(line);
      }
    }

    this.updateQuantity({
      line,
      quantity,
      action: "change",
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector("text-component")
    );
    textComponent?.shimmer();
  }

  /**
   * Finds postcard information for a given line item.
   * @param {number} line - The line number to check.
   * @returns {{hasPostcard: boolean, postcardKey: string, itemKey: string}} Postcard information.
   */
  findPostcardInfo(line) {
    const cartRow = this.refs.cartItemRows[line - 1];
    if (!cartRow) {
      return { hasPostcard: false, postcardKey: "", itemKey: "" };
    }

    // Get the item key from the row
    const itemKey = cartRow.dataset.itemKey || "";

    // Check if this item has a postcard using data attributes
    const hasPostcard = cartRow.dataset.hasPostcard === "true";
    const postcardLinkId = cartRow.dataset.postcardLinkId;

    if (!hasPostcard || !postcardLinkId) {
      return { hasPostcard: false, postcardKey: "", itemKey };
    }

    // Find the postcard item by searching all cart rows for matching link ID
    const allCartRows = this.refs.cartItemRows;
    for (let i = 0; i < allCartRows.length; i++) {
      const row = allCartRows[i];
      if (!row) continue;

      const isPostcardItem = row.dataset.postcardItem === "true";
      const postcardRowLinkId = row.dataset.postcardLinkId;

      if (isPostcardItem && postcardRowLinkId === postcardLinkId) {
        const postcardKey = row.dataset.itemKey || "";
        return { hasPostcard: true, postcardKey, itemKey };
      }
    }

    return { hasPostcard: false, postcardKey: "", itemKey };
  }

  /**
   * Handles the line item removal.
   * @param {number | {lineIndex: number, postcardKey?: string, hasPostcard?: boolean|string, itemKey?: string}} lineOrData - The line item index or data object with multiple parameters.
   * @param {Object} [event] - The event object (when called from component system).
   */
  onLineItemRemove(lineOrData, event) {
    let line, postcardKey, hasPostcard, itemKey;

    // Handle both old single parameter and new multiple parameter formats
    if (typeof lineOrData === "object" && lineOrData !== null) {
      // New format: object with multiple parameters
      ({ lineIndex: line, postcardKey, hasPostcard, itemKey } = lineOrData);
      hasPostcard = hasPostcard === "true" || hasPostcard === true;
    } else {
      // Old format: just the line number
      line = lineOrData;
      postcardKey = "";
      hasPostcard = false;
      itemKey = null;
    }

    console.log("Removing item:", { line, postcardKey, hasPostcard, itemKey });

    // Remove both items at once if there's a linked postcard
    if (hasPostcard && postcardKey && postcardKey.length > 0 && itemKey) {
      console.log(
        `Removing item with key ${itemKey} and linked postcard with key ${postcardKey}`
      );
      this.removeBothItems(itemKey, postcardKey);
    } else {
      // Remove just the single item
      this.removeSingleItem(itemKey || line);
    }
  }

  /**
   * Removes both the main item and its linked postcard using item keys.
   * @param {string} mainItemKey - The main item key.
   * @param {string} postcardKey - The postcard item key.
   */
  removeBothItems(mainItemKey, postcardKey) {
    console.log(
      `Removing item with key ${mainItemKey} and linked postcard with key ${postcardKey}`
    );

    // Find the main item line to remove the UI row
    const mainItemLine = this.findLineByKey(mainItemKey);
    if (mainItemLine) {
      // Start the animation first
      this.removeItemRowWithDelay(mainItemLine, () => {
        // After animation starts, remove from cart
        this.updateQuantityBatchByKey([
          { key: mainItemKey, quantity: 0 },
          { key: postcardKey, quantity: 0 },
        ]);
      });
    } else {
      // Fallback if we can't find the row
      this.updateQuantityBatchByKey([
        { key: mainItemKey, quantity: 0 },
        { key: postcardKey, quantity: 0 },
      ]);
    }
  }

  /**
   * Removes a single item using key or line number.
   * @param {string|number} keyOrLine - The item key or line index.
   */
  removeSingleItem(keyOrLine) {
    if (typeof keyOrLine === "string") {
      // Use key-based removal with animation
      const line = this.findLineByKey(keyOrLine);
      if (line) {
        this.removeItemRowWithDelay(line, () => {
          this.updateQuantityBatchByKey([{ key: keyOrLine, quantity: 0 }]);
        });
      } else {
        // Fallback if we can't find the row
        this.updateQuantityBatchByKey([{ key: keyOrLine, quantity: 0 }]);
      }
    } else {
      // Use line-based removal (legacy) - this already has proper animation
      this.updateQuantity({
        line: keyOrLine,
        quantity: 0,
        action: "clear",
      });

      this.removeItemRow(keyOrLine);
    }
  }

  /**
   * Removes an item row with animation, then calls callback after animation starts.
   * @param {number} line - The line index.
   * @param {Function} callback - Function to call after animation starts.
   */
  removeItemRowWithDelay(line, callback) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) {
      callback();
      return;
    }

    if (prefersReducedMotion()) {
      // No animation, call callback immediately
      callback();
      return;
    }

    // Start the animation
    cartItemRowToRemove.style.setProperty(
      "--row-height",
      `${cartItemRowToRemove.clientHeight}px`
    );
    cartItemRowToRemove.classList.add("removing");

    // Call callback after a short delay to let animation start
    setTimeout(() => {
      callback();
    }, 100);

    // Set up the actual removal after animation completes
    const remove = () => cartItemRowToRemove.remove();
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Finds the line number for a given item key.
   * @param {string} itemKey - The item key to find.
   * @returns {number|null} The line number or null if not found.
   */
  findLineByKey(itemKey) {
    const cartRows = this.refs.cartItemRows;
    if (!cartRows) return null;

    for (let i = 0; i < cartRows.length; i++) {
      const row = cartRows[i];
      if (!row) continue;

      const keyElement = /** @type {HTMLElement | null} */ (
        row.querySelector("[data-item-key]")
      );
      if (
        keyElement &&
        keyElement.dataset &&
        keyElement.dataset.itemKey === itemKey
      ) {
        return i + 1; // Convert to 1-based index
      }
    }
    return null;
  }

  /**
   * Updates multiple line items using keys in a single batch operation.
   * @param {Array<{key: string, quantity: number}>} updates - Array of key-based updates.
   */
  updateQuantityBatchByKey(updates) {
    const cartPerformaceUpdateMarker =
      cartPerformance.createStartingMarker("clear:user-action");

    this.#disableCartItems();

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component"
    );
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // Create updates object for batch removal using keys
    const updatesObject = /** @type {Record<string, number>} */ ({});
    updates.forEach((update) => {
      updatesObject[update.key] = update.quantity;
    });

    const body = JSON.stringify({
      updates: updatesObject,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_update_url}`, fetchConfig("json", { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          console.error("Cart batch update error:", parsedResponseText.errors);
          return;
        }
        console.log(
          "Cart items batch updated successfully",
          parsedResponseText
        );

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          "text/html"
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector(
          '[ref="cartItemCount"]'
        )?.textContent;
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: "cart-items-component",
            sections: parsedResponseText.sections,
          })
        );

        morphSection(
          this.sectionId,
          parsedResponseText.sections[this.sectionId]
        );
      })
      .catch((error) => {
        console.error("Cart batch update error:", error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Removes an item row from the UI with animation.
   * @param {number} line - The line index.
   */
  removeItemRow(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty(
      "--row-height",
      `${cartItemRowToRemove.clientHeight}px`
    );
    cartItemRowToRemove.classList.add("removing");

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`
    );

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component"
    );
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig("json", { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }
        console.log("Cart items updated successfully", parsedResponseText);

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          "text/html"
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector(
          '[ref="cartItemCount"]'
        )?.textContent;
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: "cart-items-component",
            sections: parsedResponseText.sections,
          })
        );

        morphSection(
          this.sectionId,
          parsedResponseText.sections[this.sectionId]
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector("input");

    if (!quantityInput) throw new Error("Quantity input not found");

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement))
      throw new Error("Cart item error not found");
    if (!(cartItemErrorContainer instanceof HTMLElement))
      throw new Error("Cart item error container not found");

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove("hidden");
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
